import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import Button from '../Buttons/Button';
import EmergencyAlertDialog from '../EmergencyAlertDialog';
import classes from './StickyButton.module.scss';
import { useFacilityContext } from '@src/components/shared/providers/FacilityContextProvider';
import { useLocationStore } from '@src/store/modules/location.store';
import { getApptTypeParam, getVisitReasonValidation } from '@src/utils/visitReasonValidation';
const StickyButton: React.FC = () => {
  const router = useRouter();
  const {
    facility: facilityDetails
  } = useFacilityContext();
  const {
    selectedTag,
    selectedPhysicalTag,
    accountIdError,
    location,
    selectedDate
  } = useLocationStore();
  const [showError, setShowError] = useState(false);
  const [isNearFooter, setIsNearFooter] = useState(false);
  const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const isHomePage = router.pathname === '/';
  const visitReasonValidation = getVisitReasonValidation(selectedTag, selectedPhysicalTag);
  const handleClick = () => {
    // Check for emergency selection first
    if (selectedTag === 'Experiencing chest pain, shortness of breath or stroke symptoms?') {
      setEmergencyDialogOpen(true);
      return;
    }
    if (!(visitReasonValidation.isValid && location)) {
      setShowError(true);
      const reasonForVisitElement = document.querySelector('[class*="hero-location"]');
      if (reasonForVisitElement) {
        reasonForVisitElement.scrollIntoView({
          behavior: 'smooth'
        });
      }
      return;
    }
    setShowError(false);
    const routerFc = router.query.fc;
    let fcFromRouter = '';
    if (typeof routerFc === 'string') {
      fcFromRouter = routerFc;
    } else if (Array.isArray(routerFc)) {
      fcFromRouter = routerFc[0] ?? '';
    }
    const fcFromUrl = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('fc') || '' : '';
    const fcFromFacility = facilityDetails?.siteId ? `WN_${facilityDetails.siteId}` : '';
    const fcParam = fcFromRouter || fcFromUrl || fcFromFacility;
    const normalizedDate = selectedDate && selectedDate !== '' ? selectedDate : new Date().toISOString().split('T')[0];
    router.push({
      pathname: '/results',
      query: {
        ...(fcParam ? {
          fc: fcParam
        } : {}),
        'appt-type': getApptTypeParam(selectedTag, selectedPhysicalTag),
        date: normalizedDate
      }
    });
  };
  useEffect(() => {
    const checkFooterVisibility = () => {
      const footer = document.querySelector('footer');
      if (!footer) return;
      const footerRect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      if (footerRect.top < windowHeight + 100) {
        setIsNearFooter(true);
        document.body.style.paddingBottom = '0';
      } else {
        setIsNearFooter(false);
        if (buttonContainerRef.current) {
          const buttonHeight = buttonContainerRef.current.offsetHeight;
          document.body.style.paddingBottom = `${buttonHeight}px`;
        }
      }
    };
    checkFooterVisibility();
    window.addEventListener('scroll', checkFooterVisibility);
    window.addEventListener('resize', checkFooterVisibility);
    return () => {
      window.removeEventListener('scroll', checkFooterVisibility);
      window.removeEventListener('resize', checkFooterVisibility);
      document.body.style.paddingBottom = '0';
    };
  }, []);
  if (!isHomePage || accountIdError) return null;
  return <>
			<div ref={buttonContainerRef} className={`${classes.stickyButtonContainer} ${isNearFooter ? classes.hidden : ''}`}>
				{showError && visitReasonValidation.errorMessage && <div className={classes.errorMessage}>{visitReasonValidation.errorMessage}</div>}
				<Button className={classes.stickyButton} disabled={false} onClick={handleClick} data-sentry-element="Button" data-sentry-source-file="StickyButton.tsx">
					Schedule your Visit
				</Button>
			</div>
			<EmergencyAlertDialog open={emergencyDialogOpen} onClose={() => setEmergencyDialogOpen(false)} data-sentry-element="EmergencyAlertDialog" data-sentry-source-file="StickyButton.tsx" />
		</>;
};
export default StickyButton;